import * as React from 'react';
import styled from 'styled-components';
import { pipe } from 'fp-ts/lib/function';
import * as E from 'fp-ts/lib/Either';

import * as I from '../../../icons';
import { GLOBAL_INPUT_CLASS } from '../../../core/Input';
import { splitClasses } from '../../utils';
import { useControl, Model } from '..';
import { Input } from '../shared';

declare module '../../Controls/hkt' {
    interface URIControlsToKind {
        'number': IInputProps
    }
}

type IInputProps = {
    name: string,
    onChange: (value: number, name: string) => void,
    value: number,
    min?: number,
    max?: number,
    step?: number,
    placeholder?: string,
    disabled?: boolean,
    onFocus?: () => void,
    onBlur?: () => void,
    className?: string,
}


const inRange = (min: number, max: number) => (x: number) => Math.min(Math.max(x, min), max);

const basicModel = (min: number, max: number): Model<number> => {
    return {
        empty: 0,
        putOut: (str, prev) => pipe(
            str,
            s => s === '' ? 0 : parseFloat(str),
            r => isNaN(r) ? E.left(prev) : E.right(r),
            E.map(inRange(min, max)),
            E.mapLeft(x => x === null ? min : x),
            E.fold(
                (l) => l,
                (r) => r,
            )
        ),
        putIn: (number) => {
            return String(inRange(min, max)(number));
        },
    }
}

const Control = React.forwardRef<HTMLInputElement, IInputProps>(({ 
    name,
    onChange,
    value,
    disabled = false,
    className,
    min = -Infinity,
    max = Infinity,
    step = 1,
}, ref) => {
    const [isFocus, updateFocus] = React.useState(false);
    const focusin = () => updateFocus(true);
    const focusout = () => updateFocus(false);

    const {
        value: localValue,
        displayValue,
        put,
        inoutput,
    } = useControl(basicModel(min, max), value);

    const selfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const x = e.target.value;
        inoutput(x);
    }

    const increment = () => {
        const value = String(localValue + step);
        inoutput(value);
    };

    const decrement = () => {
        const value = String(localValue - step);
        inoutput(value);
    }

    const onFocus = () => {
        focusin();
    }

    const onBlur = () => {
        focusout();
    }

    React.useEffect(() => {
        onChange(localValue, name);
    }, [localValue]);


    React.useEffect(() => {
        if(isFocus && displayValue === '0'){
            put('');
        }
        else if(!isFocus && displayValue === ''){
            put('0');
        }
    }, [isFocus, displayValue]);

    return (
        <Container className={splitClasses(GLOBAL_INPUT_CLASS, className)}>
            <OverrideInput
                name={name}
                onChange={selfChange}
                disabled={disabled}
                value={displayValue}
                onFocus={onFocus}
                onBlur={onBlur}
                ref={ref}
            />
            <Controls>
                <Inc onClick={increment}>
                    <I.Arrow2 />
                </Inc>
                <Dec onClick={decrement}>
                    <I.Arrow2 />
                </Dec>
            </Controls>
        </Container>
    );
});

export default Control;

const OverrideInput = styled(Input)`
    padding-right: 20px;
    height: 100%;
`;


const Container = styled.div`
    position: relative;
`;


const Controls = styled.div`
    width: 9px;
    height: calc(100% - 12px);
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
`;

const Btn = styled.button`
    cursor: pointer;
    position: absolute;
    height: 50%;
    width: 100%;
    margin: 0;
    background-color: transparent;
    display: inline-block;
    background-clip: border-box;
    border: 1px solid #C9CCD6;
    padding: 0;

    svg {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        fill: #898F9E;
    }

    &:hover {
        background-color: #898F9E;
        border-color: #898F9E;

        svg {
            fill: #fff;
        }
    }
`;

const Inc = styled(Btn)`
    top: 0;
    border-radius: 3px 3px 0 0;
    border-bottom: 0;
`;

const Dec = styled(Btn)`
    bottom: 0;
    border-radius: 0 0 3px 3px;
    border-top: 0;

    svg {
        transform: rotate(180deg);
    }
`;